export interface PromptField {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autocomplete?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  fields?: PromptField[];
  submitLabel?: string;
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

/**
 * Petite boîte de dialogue réutilisable. Renvoie les valeurs saisies, ou
 * `null` si l'utilisateur annule.
 */
export function promptUser(options: PromptOptions): Promise<Record<string, string> | null> {
  const dialog = byId<HTMLDialogElement>('prompt-dialog');
  const form = byId<HTMLFormElement>('prompt-form');
  const title = byId<HTMLHeadingElement>('prompt-title');
  const message = byId<HTMLParagraphElement>('prompt-message');
  const fieldsHost = byId<HTMLDivElement>('prompt-fields');
  const submit = byId<HTMLButtonElement>('prompt-ok');
  const cancel = byId<HTMLButtonElement>('prompt-cancel');

  title.textContent = options.title;
  message.textContent = options.message ?? '';
  message.hidden = !options.message;
  fieldsHost.replaceChildren();
  submit.textContent = options.submitLabel ?? 'Valider';

  const inputs = new Map<string, HTMLInputElement>();
  for (const field of options.fields ?? []) {
    const label = document.createElement('label');
    label.className = 'field';
    label.textContent = field.label;
    const input = document.createElement('input');
    input.type = field.type ?? 'text';
    input.spellcheck = false;
    if (field.placeholder) input.placeholder = field.placeholder;
    if (field.autocomplete) input.setAttribute('autocomplete', field.autocomplete);
    label.appendChild(input);
    fieldsHost.appendChild(label);
    inputs.set(field.id, input);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (values: Record<string, string> | null): void => {
      if (settled) return;
      settled = true;
      form.removeEventListener('submit', onSubmit);
      cancel.removeEventListener('click', onCancel);
      dialog.removeEventListener('close', onClose);
      if (dialog.open) dialog.close();
      resolve(values);
    };
    const onSubmit = (event: SubmitEvent): void => {
      event.preventDefault();
      const values: Record<string, string> = {};
      for (const [id, input] of inputs) values[id] = input.value;
      finish(values);
    };
    const onCancel = (): void => finish(null);
    const onClose = (): void => finish(null);

    form.addEventListener('submit', onSubmit);
    cancel.addEventListener('click', onCancel);
    dialog.addEventListener('close', onClose);
    dialog.showModal();
    inputs.values().next().value?.focus();
  });
}

export async function showInfo(title: string, message: string): Promise<void> {
  await promptUser({ title, message, submitLabel: 'OK' });
}
